import { formatMoney } from './money'

/**
 * The vocabulary a notification kind's filters are written in, and the one
 * rule for whether a notice matches a person's choice.
 *
 * The engine understands these without knowing what any of them mean: a
 * `oneOf` filter over campaign drives and one over rooms are the same thing to
 * it. Shared, because the settings screen states a choice in the same terms
 * the engine judges it by.
 */

/**
 * The value a `oneOf` choice holds for "a notice about none of these" — general
 * giving, for a filter over campaigns. Never an option's own value, which is
 * the context's id for something real.
 */
export const NOTIFICATION_NONE = '(none)'

/**
 * The value a `oneOf` choice holds for "any of these, but not none" — every
 * campaign and no general giving. Stored as itself rather than as a list of
 * today's options, so an option the org adds later is included.
 */
export const NOTIFICATION_ANY = '(any)'

/**
 * The units a figure in a notification is in. A unit is part of the
 * vocabulary: a new one is added here, and every switch over it — the screen's
 * field, the roll-up's totals and sheet — refuses to compile until it is drawn.
 */
export type NotificationUnit = 'cents'

/** A figure as a person reads it, in its unit. */
export function formatFigure(unit: NotificationUnit, n: number): string {
  switch (unit) {
    case 'cents':
      return formatMoney(n)
    default: {
      const unhandled: never = unit
      return unhandled
    }
  }
}

/**
 * How often a roll-up arrives, each in the reader's own zone: on the hour, at
 * 7 in the morning or the evening, or on Sunday at 7 in the morning — the
 * week's first morning. Stated here so the screen's labels and the engine's
 * periods name the same windows.
 */
export const ROLLUP_SCHEDULES = [
  'hourly',
  'daily_am',
  'daily_pm',
  'weekly',
] as const
export type RollupSchedule = (typeof ROLLUP_SCHEDULES)[number]

/** The hour a daily or weekly roll-up arrives at, in the reader's zone. */
export const ROLLUP_HOUR = { daily_am: 7, daily_pm: 19, weekly: 7 } as const

/** A schedule as a person reads it. */
export const ROLLUP_SCHEDULE_LABEL: Record<RollupSchedule, string> = {
  hourly: 'Hourly',
  daily_am: 'Daily, 7 am',
  daily_pm: 'Daily, 7 pm',
  weekly: 'Weekly, Sunday 7 am',
}

export type NotificationFilterSpec =
  | {
      key: string
      type: 'oneOf'
      label: string
      /** The label for a notice carrying no value; omitted where there is none. */
      noneLabel?: string
    }
  | {
      key: string
      type: 'atLeast'
      label: string
      unit: NotificationUnit
    }

/** What a notice carries for its kind's filters, keyed by filter. */
export type NotificationFacets = Record<string, string[] | number>

/** A person's answer for each filter, keyed by filter. Absent means everything. */
export type NotificationChoiceFilters = Record<string, string[] | number>

/**
 * Whether a notice matches a choice.
 *
 * Every declared filter must pass. A filter the person left unanswered passes
 * everything. `oneOf` passes when any value the notice carries was chosen, when
 * it carries any and ANY was chosen, or when it carries none and NONE was
 * chosen. `atLeast` passes when the notice's measure reaches the floor; a
 * notice without one does not.
 */
export function matchesChoice(
  filters: readonly NotificationFilterSpec[],
  facets: NotificationFacets,
  chosen: NotificationChoiceFilters,
): boolean {
  return filters.every((f) => {
    const answer = chosen[f.key]
    if (answer === undefined) return true
    const facet = facets[f.key]
    if (f.type === 'oneOf') {
      if (!Array.isArray(answer)) return false
      const values = Array.isArray(facet) ? facet : []
      if (values.length === 0) return answer.includes(NOTIFICATION_NONE)
      if (answer.includes(NOTIFICATION_ANY)) return true
      return values.some((v) => answer.includes(v))
    }
    if (typeof answer !== 'number') return false
    return typeof facet === 'number' && facet >= answer
  })
}

/**
 * The problem with a choice's filters against what the kind declares, or null
 * when it is sound. `options` are the values a `oneOf` filter offers at this
 * org; a value outside them is refused rather than stored, since a choice
 * naming something that is not there matches nothing and says so to nobody.
 */
export function choiceFiltersProblem(
  filters: readonly NotificationFilterSpec[],
  options: Record<string, readonly string[]>,
  chosen: NotificationChoiceFilters,
): string | null {
  const declared = new Map(filters.map((f) => [f.key, f]))
  for (const [key, answer] of Object.entries(chosen)) {
    const f = declared.get(key)
    if (!f) return `This notification has no filter called "${key}".`
    if (f.type === 'oneOf') {
      if (!Array.isArray(answer)) return `${f.label} takes a list of choices.`
      const allowed = new Set(options[key] ?? [])
      allowed.add(NOTIFICATION_ANY)
      if (f.noneLabel) allowed.add(NOTIFICATION_NONE)
      const unknown = answer.find((v) => !allowed.has(v))
      if (unknown !== undefined) {
        return `${f.label} offers no choice "${unknown}" here.`
      }
    } else if (
      typeof answer !== 'number' ||
      !Number.isSafeInteger(answer) ||
      answer < 0
    ) {
      return `${f.label} takes a whole number of zero or more.`
    }
  }
  return null
}
